
import React, { useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { UserRole } from '@/lib/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RoleChangeDialog } from './RoleChangeDialog';

interface UserRoleSelectProps {
  userId: string;
  currentRole: UserRole;
  onRoleChange: (userId: string, newRole: UserRole) => void;
  userName: string;
}

export const UserRoleSelect: React.FC<UserRoleSelectProps> = ({ 
  userId, 
  currentRole,
  onRoleChange,
  userName
}) => {
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [pendingRole, setPendingRole] = useState<UserRole | null>(null);
  const [isChanging, setIsChanging] = useState(false);

  const handleRoleSelect = (value: string) => {
    const validRoles: UserRole[] = ['admin', 'manager', 'sales', 'member'];
    
    if (!validRoles.includes(value as UserRole)) {
      toast({
        title: "Invalid role",
        description: `${value} is not a valid role`,
        variant: "destructive",
      });
      return;
    }
    
    const newRole = value as UserRole;
    
    // Open dialog for confirmation and reason
    setPendingRole(newRole);
    setIsDialogOpen(true);
  };

  const handleConfirmRoleChange = async (reason?: string) => {
    if (!pendingRole) return;
    
    setIsChanging(true);
    try {
      // RPC now handles both role change AND audit logging atomically
      const { error } = await supabase.rpc('admin_assign_role', {
        p_user_id: userId,
        p_role: pendingRole,
        p_reason: reason || null
      });

      if (error) throw error;

      onRoleChange(userId, pendingRole);

      toast({
        title: "Role updated",
        description: `User role has been updated to ${pendingRole}`,
      });
      
      setIsDialogOpen(false);
      setPendingRole(null);
    } catch (error: any) {
      toast({
        title: "Failed to update role",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <>
      <Select
        value={currentRole}
        onValueChange={handleRoleSelect}
        disabled={isChanging}
      >
        <SelectTrigger className="w-[120px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="z-50 bg-background">
          <SelectItem value="admin">Admin</SelectItem>
          <SelectItem value="manager">Manager</SelectItem>
          <SelectItem value="sales">Sales</SelectItem>
          <SelectItem value="member">Member</SelectItem>
        </SelectContent>
      </Select>

      <RoleChangeDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        userName={userName}
        currentRole={currentRole}
        newRole={pendingRole || currentRole}
        onConfirm={handleConfirmRoleChange}
        isLoading={isChanging}
      />
    </>
  );
};
